import React, { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router';

import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import type { EstadoCofre } from '@actual-app/core/server/encryption/cofre-app';

import { setAppState } from '#app/appSlice';
import { ProtectedRoute } from '#auth/ProtectedRoute';
import { Permissions } from '#auth/types';
import { downloadBudget, loadBudget } from '#budgetfiles/budgetfilesSlice';
import {
  BackToFileListButton,
  UserDirectoryPage,
} from '#components/admin/UserDirectory/UserDirectoryPage';
import { AppBackground } from '#components/AppBackground';
import { PantallaCofre } from '#components/cuenta/PantallaCofre';
import { LoggedInUser } from '#components/LoggedInUser';
import { Notifications } from '#components/Notifications';
import { useMultiuserEnabled } from '#components/ServerContext';
import { useMetaThemeColor } from '#hooks/useMetaThemeColor';
import { useNavigate } from '#hooks/useNavigate';
import {
  consumirConfigServidor,
  consumirListaSolicitada,
  primeraAperturaAutomatica,
  SOLO_UN_PRESUPUESTO,
} from '#presupuesto';
import { useDispatch, useSelector } from '#redux';
import { loggedIn } from '#users/usersSlice';

import { BudgetFileSelection } from './BudgetFileSelection';
import { ConfigServer } from './ConfigServer';
import { Bootstrap } from './subscribe/Bootstrap';
import { ChangePassword } from './subscribe/ChangePassword';
import { Error } from './subscribe/Error';
import { Login } from './subscribe/Login';
import { OpenIdCallback } from './subscribe/OpenIdCallback';
import { WelcomeScreen } from './WelcomeScreen';

export function ManagementApp() {
  useMetaThemeColor(theme.mobileConfigServerViewTheme);

  const files = useSelector(state => state.budgetfiles.allFiles);
  const isLoading = useSelector(state => state.app.loadingText !== null);
  const userData = useSelector(state => state.user.data);
  const multiuserEnabled = useMultiuserEnabled();

  const managerHasInitialized = useSelector(
    state => state.app.managerHasInitialized,
  );

  const dispatch = useDispatch();

  // runs on mount only
  useEffect(() => {
    async function fetchData() {
      await dispatch(loggedIn());
      dispatch(setAppState({ managerHasInitialized: true }));
    }

    void fetchData();
  }, [dispatch]);

  // Rumbo: con servidor, el cofre de la persona tiene que estar abierto en
  // este aparato antes de abrir ningún presupuesto (cifrado obligatorio).
  const [estadoCofre, setEstadoCofre] = useState<EstadoCofre | null>(null);
  useEffect(() => {
    if (!managerHasInitialized || !userData) {
      setEstadoCofre(null);
      return;
    }
    let vigente = true;
    void send('cofre-estado').then(({ estado }) => {
      if (vigente) {
        setEstadoCofre(estado);
      }
    });
    return () => {
      vigente = false;
    };
  }, [managerHasInitialized, userData]);
  const cofrePendiente =
    estadoCofre === 'sin-llavero' || estadoCofre === 'cerrado';
  const cofreDecidido = estadoCofre !== null && !cofrePendiente;

  const autoAperturaDecidida = useRef(false);
  // Presupuesto: Ajustes → «Servidor» pide abrir la configuración del servidor.
  const navigate = useNavigate();
  const configServidorConsumida = useRef(false);
  useEffect(() => {
    if (
      configServidorConsumida.current ||
      !managerHasInitialized ||
      isLoading
    ) {
      return;
    }
    configServidorConsumida.current = true;
    if (consumirConfigServidor()) {
      autoAperturaDecidida.current = true;
      void navigate('/config-server');
    }
  }, [isLoading, managerHasInitialized, navigate]);

  // Presupuesto: con un solo archivo se abre directamente, salvo que el
  // usuario haya pedido ver la lista («Cambiar de archivo»).
  useEffect(() => {
    if (!SOLO_UN_PRESUPUESTO || autoAperturaDecidida.current) {
      return;
    }
    if (
      !managerHasInitialized ||
      isLoading ||
      !userData ||
      !files ||
      !cofreDecidido
    ) {
      return;
    }
    autoAperturaDecidida.current = true;
    const listaSolicitada = consumirListaSolicitada();
    if (!primeraAperturaAutomatica() || listaSolicitada || files.length !== 1) {
      return;
    }
    const [unico] = files;
    if (unico.state === 'remote') {
      void dispatch(downloadBudget({ cloudFileId: unico.cloudFileId }));
    } else {
      void dispatch(loadBudget({ id: unico.id }));
    }
  }, [
    cofreDecidido,
    dispatch,
    files,
    isLoading,
    managerHasInitialized,
    userData,
  ]);

  return (
    <View style={{ height: '100%', color: theme.pageText }}>
      <AppBackground />
      <View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 40,
          WebkitAppRegion: 'drag',
        }}
      />
      <View
        style={{
          position: 'absolute',
          bottom: 40,
          right: 15,
        }}
      >
        <Notifications
          style={{
            position: 'relative',
            left: 'initial',
            right: 'initial',
          }}
        />
      </View>

      {managerHasInitialized && !isLoading && (
        <View
          style={{
            alignItems: 'center',
            bottom: 0,
            justifyContent: 'center',
            left: 0,
            padding: 20,
            position: 'absolute',
            right: 0,
            top: 0,
          }}
        >
          {userData && files && cofrePendiente ? (
            <PantallaCofre
              estado={estadoCofre}
              onListo={() => setEstadoCofre('abierto')}
            />
          ) : userData && files ? (
            <>
              <Routes>
                <Route path="/config-server" element={<ConfigServer />} />

                <Route path="/change-password" element={<ChangePassword />} />
                {files && files.length > 0 ? (
                  <Route path="/" element={<BudgetFileSelection />} />
                ) : (
                  <Route path="/" element={<WelcomeScreen />} />
                )}

                {multiuserEnabled && (
                  <Route
                    path="/user-directory"
                    element={
                      <ProtectedRoute
                        permission={Permissions.ADMINISTRATOR}
                        element={
                          <UserDirectoryPage
                            bottomContent={<BackToFileListButton />}
                          />
                        }
                      />
                    }
                  />
                )}
                {/* Redirect all other pages to this route */}
                <Route path="/*" element={<Navigate to="/" replace />} />
              </Routes>

              <View
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  padding: '6px 10px',
                  zIndex: 4000,
                }}
              >
                <Routes>
                  <Route path="/config-server" element={null} />
                  <Route
                    path="/*"
                    element={
                      <LoggedInUser
                        hideIfNoServer
                        style={{ padding: '4px 7px' }}
                      />
                    }
                  />
                </Routes>
              </View>
            </>
          ) : (
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/openid-cb" element={<OpenIdCallback />} />
              <Route path="/error" element={<Error />} />
              <Route path="/config-server" element={<ConfigServer />} />
              <Route path="/bootstrap" element={<Bootstrap />} />
              {multiuserEnabled && (
                <Route
                  path="/userdirectory"
                  element={
                    <ProtectedRoute
                      permission={Permissions.ADMINISTRATOR}
                      element={<UserDirectoryPage />}
                    />
                  }
                />
              )}

              {/* Redirect all other pages to this route */}
              <Route path="/*" element={<Navigate to="/bootstrap" replace />} />
            </Routes>
          )}
        </View>
      )}
    </View>
  );
}
